/**
 * unifiedToActivityItem
 *
 * Adaptador puro que converte um `UnifiedCalendarEvent` (Calendário Global)
 * no shape `ActivityItem` esperado pelo componente `SchedulingListView`.
 *
 * Além do mapeamento básico de tipo, fornece um `iconNode` e `dotColor`
 * específicos por FONTE — assim cada origem (manual / google / tarefa /
 * mensagem agendada / agendamento online / atividade CRM) é distinguível
 * "batendo o olho" na listagem.
 *
 * RELEASE:
 *   2026-10-09 v1.2.0 - Adiciona Ordem de Serviço à adaptação de lista.
 *   2026-06-25 v1.1.0 - Ícone + cor por fonte (override visual no list view)
 *   2026-06-25 v1.0.0 - Versão inicial
 */

import { createElement, type ReactNode } from 'react';
import type {
  UnifiedCalendarEvent,
  CalendarEventSourceType,
} from '@/hooks/useCalendarUnifiedEvents';
import { SOURCE_ICON_META, SOURCE_LABELS } from './sourceMeta';

/** Tipo aceito pelo SchedulingListView */
export interface ListActivityItem {
  id: string;
  title: string;
  description: string | null;
  type: 'note' | 'call' | 'meeting' | 'task' | 'email';
  scheduled_at: string;
  completed_at: string | null;
  performed_by_user_id: string | null;
  deal_id: string | null;
  deal_title?: string;
  customer_name?: string;
  assigned_user_name?: string;
  conferencing_link?: string | null;
  is_standalone_booking?: boolean;
  /** Ícone customizado por fonte (override no SchedulingListView) */
  iconNode?: ReactNode;
  /** Cor do indicador (bolinha) por fonte */
  dotColor?: string;
  /** Tooltip do ícone customizado */
  iconTitle?: string;
}

/** Mapeia a fonte para um tipo de atividade compatível (compat). */
function mapSourceToType(
  source: CalendarEventSourceType,
): ListActivityItem['type'] {
  switch (source) {
    case 'task':
      return 'task';
    case 'service_order':
      return 'task';
    case 'scheduled_message':
      return 'note';
    case 'activity':
    case 'manual':
    case 'google':
    case 'booking':
    default:
      return 'meeting';
  }
}

/** Converte um evento unificado em item de lista. */
export function unifiedToActivityItem(
  event: UnifiedCalendarEvent,
): ListActivityItem {
  const meta = SOURCE_ICON_META[event.source_type] ?? SOURCE_ICON_META.manual;
  const label = SOURCE_LABELS[event.source_type] ?? SOURCE_LABELS.manual;
  // Cor lida via HSL do token semântico (compatível com tema claro/escuro)
  const dotColor = `hsl(var(${meta.cssVar}))`;
  // Ícone customizado renderizado com cor herdada do token
  const iconNode = createElement(meta.Icon, {
    className: 'h-4 w-4',
    style: { color: dotColor },
  });

  // v1.2.0: reflete concluído/cancelado vindo da RPC unificada
  const completedAt = event.completed_at ?? event.cancelled_at ?? null;
  // Prefixo "❌ CANCELADO" mantém paridade com SchedulingListView (badge "Cancelado")
  const baseDescription = event.description ?? null;
  const description = event.cancelled_at && !baseDescription?.startsWith('❌ CANCELADO')
    ? `❌ CANCELADO${baseDescription ? ' — ' + baseDescription : ''}`
    : baseDescription;

  return {
    id: event.id,
    title: event.title,
    description,
    type: mapSourceToType(event.source_type),
    scheduled_at: event.start_at,
    completed_at: completedAt,
    performed_by_user_id: null,
    deal_id: event.deal_id ?? null,
    deal_title: event.deal_title ?? undefined,
    customer_name: event.customer_name ?? undefined,
    conferencing_link: event.meet_link ?? null,
    iconNode,
    dotColor,
    iconTitle: label,
  };
}
